/**
 * Layout classification for responsive UI.
 * "Mobile layout" means phone — not merely a narrow desktop window.
 */
export const MOBILE_LAYOUT_MAX_SHORT_EDGE_PX = 768;

/**
 * @param {string} ua
 * @param {{ platform?: string, maxTouchPoints?: number }} [env]
 * @returns {boolean}
 */
export function isTabletUserAgent(ua, env = {}) {
  const platform = env.platform ?? '';
  const maxTouchPoints = env.maxTouchPoints ?? 0;

  if (/iPad/i.test(ua)) return true;
  // iPadOS 13+ reports as Mac with touch
  if (platform === 'MacIntel' && maxTouchPoints > 1) return true;
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
  if (/Silk|Kindle|PlayBook/i.test(ua)) return true;

  return false;
}

/**
 * Detect a phone (not tablet, not desktop) using UA/client hints and, as a last
 * resort, touch-primary input with a phone-sized viewport.
 *
 * @param {number} [innerWidth]
 * @param {number} [innerHeight]
 * @returns {boolean}
 */
export function detectPhoneDevice(innerWidth, innerHeight) {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;

  if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
    return navigator.userAgentData.mobile;
  }

  if (isTabletUserAgent(ua, { platform, maxTouchPoints })) {
    return false;
  }

  if (/iPhone|iPod/i.test(ua)) return true;
  if (/Android/i.test(ua)) return /Mobile/i.test(ua);
  if (/IEMobile|Windows Phone|BlackBerry|Opera Mini|webOS/i.test(ua)) return true;

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const touchPrimary = window.matchMedia('(pointer: coarse) and (hover: none)').matches;
    if (touchPrimary) {
      const w = innerWidth ?? window.innerWidth;
      const h = innerHeight ?? window.innerHeight;
      const shortEdge = Math.min(w, h);
      if (shortEdge < MOBILE_LAYOUT_MAX_SHORT_EDGE_PX) return true;
    }
  }

  return false;
}

/**
 * @param {number} innerWidth
 * @param {number} innerHeight
 * @returns {{
 *   innerWidth: number,
 *   innerHeight: number,
 *   shortEdge: number,
 *   longEdge: number,
 *   isPhone: boolean,
 *   isMobileLayout: boolean,
 *   isDesktopLayout: boolean,
 *   isLandscape: boolean,
 *   isPortrait: boolean,
 * }}
 */
export function computeDeviceLayout(innerWidth, innerHeight) {
  const shortEdge = Math.min(innerWidth, innerHeight);
  const longEdge = Math.max(innerWidth, innerHeight);
  const isPhone = detectPhoneDevice(innerWidth, innerHeight);
  const isMobileLayout = isPhone;
  const isLandscape = innerWidth >= innerHeight;

  return {
    innerWidth,
    innerHeight,
    shortEdge,
    longEdge,
    isPhone,
    isMobileLayout,
    isDesktopLayout: !isMobileLayout,
    isLandscape,
    isPortrait: !isLandscape,
  };
}
